/**
 * Sorting & Grouping Configuration Component
 * Visual interface for configuring Excel sorting and grouping
 */

import { useState } from 'react';
import './SortingGroupingConfig.css';

const SortingGroupingConfig = ({ value, onChange, availableColumns = [] }) => {
    const [activeTab, setActiveTab] = useState('sorting');

    const config = {
        sortBy: value?.sortBy || [],
        groupBy: value?.groupBy || '',
        outputType: value?.outputType || 'single_sheet_grouped',
        showSectionHeaders: value?.showSectionHeaders !== undefined ? value.showSectionHeaders : true,
        showSubtotals: value?.showSubtotals !== undefined ? value.showSubtotals : true,
        showGrandTotal: value?.showGrandTotal !== undefined ? value.showGrandTotal : true,
        colorScheme: value?.colorScheme || 'default'
    };

    const updateConfig = (updates) => {
        onChange({ ...config, ...updates });
    };

    const addSortColumn = () => {
        updateConfig({
            sortBy: [...config.sortBy, { column: '', order: 'asc' }]
        });
    };

    const updateSortColumn = (index, field, value) => {
        const newSortBy = config.sortBy.map((sort, i) =>
            i === index ? { ...sort, [field]: value } : sort
        );
        updateConfig({ sortBy: newSortBy });
    };

    const removeSortColumn = (index) => {
        updateConfig({
            sortBy: config.sortBy.filter((_, i) => i !== index)
        });
    };

    const moveSortColumn = (index, direction) => {
        const newSortBy = [...config.sortBy];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < newSortBy.length) {
            [newSortBy[index], newSortBy[newIndex]] = [newSortBy[newIndex], newSortBy[index]];
            updateConfig({ sortBy: newSortBy });
        }
    };

    return (
        <div className="sorting-grouping-config">
            <div className="config-tabs">
                <button
                    className={`tab-button ${activeTab === 'sorting' ? 'active' : ''}`}
                    onClick={() => setActiveTab('sorting')}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M7 12h10M10 18h4" />
                    </svg>
                    Sorting
                </button>
                <button
                    className={`tab-button ${activeTab === 'grouping' ? 'active' : ''}`}
                    onClick={() => setActiveTab('grouping')}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                    </svg>
                    Grouping
                </button>
                <button
                    className={`tab-button ${activeTab === 'output' ? 'active' : ''}`}
                    onClick={() => setActiveTab('output')}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                    </svg>
                    Output
                </button>
            </div>

            <div className="config-content">
                {/* Sorting Tab */}
                {activeTab === 'sorting' && (
                    <div className="config-panel">
                        <div className="panel-header">
                            <h3>Sort Columns</h3>
                            <p>Define the order in which data should be sorted</p>
                        </div>

                        <div className="sort-list">
                            {config.sortBy.length === 0 ? (
                                <div className="empty-state">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M3 6h18M7 12h10M10 18h4" />
                                    </svg>
                                    <p>No sorting rules defined</p>
                                    <span>Add a column to start sorting</span>
                                </div>
                            ) : (
                                config.sortBy.map((sort, index) => (
                                    <div key={index} className="sort-item">
                                        <div className="sort-item-header">
                                            <span className="sort-priority">#{index + 1}</span>
                                            <div className="sort-controls">
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => moveSortColumn(index, 'up')}
                                                    disabled={index === 0}
                                                    title="Move up"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="18 15 12 9 6 15" />
                                                    </svg>
                                                </button>
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => moveSortColumn(index, 'down')}
                                                    disabled={index === config.sortBy.length - 1}
                                                    title="Move down"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="6 9 12 15 18 9" />
                                                    </svg>
                                                </button>
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={() => removeSortColumn(index)}
                                                    title="Remove"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="sort-item-body">
                                            <div className="form-group">
                                                <label>Column</label>
                                                <select
                                                    value={sort.column}
                                                    onChange={(e) => updateSortColumn(index, 'column', e.target.value)}
                                                    className="form-select"
                                                >
                                                    <option value="">Select column...</option>
                                                    {availableColumns.map(col => (
                                                        <option key={col} value={col}>{col}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="form-group">
                                                <label>Order</label>
                                                <div className="radio-group">
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            name={`order-${index}`}
                                                            value="asc"
                                                            checked={sort.order === 'asc'}
                                                            onChange={(e) => updateSortColumn(index, 'order', e.target.value)}
                                                        />
                                                        <span>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M12 5v14M5 12l7-7 7 7" />
                                                            </svg>
                                                            Ascending (A-Z, 0-9)
                                                        </span>
                                                    </label>
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            name={`order-${index}`}
                                                            value="desc"
                                                            checked={sort.order === 'desc'}
                                                            onChange={(e) => updateSortColumn(index, 'order', e.target.value)}
                                                        />
                                                        <span>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M12 19V5M5 12l7 7 7-7" />
                                                            </svg>
                                                            Descending (Z-A, 9-0)
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <button className="btn-add" onClick={addSortColumn}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Add Sort Column
                        </button>
                    </div>
                )}

                {/* Grouping Tab */}
                {activeTab === 'grouping' && (
                    <div className="config-panel">
                        <div className="panel-header">
                            <h3>Group Data</h3>
                            <p>Organize data into sections with headers and subtotals</p>
                        </div>

                        <div className="form-group">
                            <label>Group By Column</label>
                            <select
                                value={config.groupBy}
                                onChange={(e) => updateConfig({ groupBy: e.target.value })}
                                className="form-select"
                            >
                                <option value="">No grouping</option>
                                {availableColumns.map(col => (
                                    <option key={col} value={col}>{col}</option>
                                ))}
                            </select>
                            <span className="form-hint">
                                Select a column to group data by (e.g., Class, Department, Category)
                            </span>
                        </div>

                        {config.groupBy && (
                            <>
                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={config.showSectionHeaders}
                                            onChange={(e) => updateConfig({ showSectionHeaders: e.target.checked })}
                                        />
                                        <span>Show Section Headers</span>
                                    </label>
                                    <span className="form-hint">
                                        Display color-coded headers for each group
                                    </span>
                                </div>

                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={config.showSubtotals}
                                            onChange={(e) => updateConfig({ showSubtotals: e.target.checked })}
                                        />
                                        <span>Show Subtotals</span>
                                    </label>
                                    <span className="form-hint">
                                        Calculate and display subtotals for each group
                                    </span>
                                </div>

                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={config.showGrandTotal}
                                            onChange={(e) => updateConfig({ showGrandTotal: e.target.checked })}
                                        />
                                        <span>Show Grand Total</span>
                                    </label>
                                    <span className="form-hint">
                                        Display overall totals across all groups
                                    </span>
                                </div>

                                <div className="preview-box">
                                    <div className="preview-header">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                        Preview
                                    </div>
                                    <div className="preview-content">
                                        {config.showSectionHeaders && (
                                            <div className="preview-section-header">
                                                === {config.groupBy} Group 1 ===
                                            </div>
                                        )}
                                        <div className="preview-rows">
                                            <div className="preview-row">Row 1 data...</div>
                                            <div className="preview-row">Row 2 data...</div>
                                            <div className="preview-row">Row 3 data...</div>
                                        </div>
                                        {config.showSubtotals && (
                                            <div className="preview-subtotal">
                                                Subtotal: 1,234.56
                                            </div>
                                        )}
                                        {config.showGrandTotal && (
                                            <div className="preview-grand-total">
                                                Grand Total: 5,678.90
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Output Tab */}
                {activeTab === 'output' && (
                    <div className="config-panel">
                        <div className="panel-header">
                            <h3>Output Format</h3>
                            <p>Choose how the Excel file should be structured</p>
                        </div>

                        <div className="form-group">
                            <label>Output Type</label>
                            <div className="output-type-grid">
                                <label className={`output-type-card ${config.outputType === 'single_sheet_grouped' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="outputType"
                                        value="single_sheet_grouped"
                                        checked={config.outputType === 'single_sheet_grouped'}
                                        onChange={(e) => updateConfig({ outputType: e.target.value })}
                                    />
                                    <div className="output-type-icon">
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <line x1="3" y1="9" x2="21" y2="9" />
                                            <line x1="3" y1="15" x2="21" y2="15" />
                                        </svg>
                                    </div>
                                    <div className="output-type-info">
                                        <strong>Single Sheet (Grouped)</strong>
                                        <span>All data in one sheet with color-coded sections</span>
                                    </div>
                                </label>

                                <label className={`output-type-card ${config.outputType === 'separate_sheets' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="outputType"
                                        value="separate_sheets"
                                        checked={config.outputType === 'separate_sheets'}
                                        onChange={(e) => updateConfig({ outputType: e.target.value })}
                                        disabled={!config.groupBy}
                                    />
                                    <div className="output-type-icon">
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <rect x="3" y="3" width="7" height="7" rx="1" />
                                            <rect x="14" y="3" width="7" height="7" rx="1" />
                                            <rect x="3" y="14" width="7" height="7" rx="1" />
                                            <rect x="14" y="14" width="7" height="7" rx="1" />
                                        </svg>
                                    </div>
                                    <div className="output-type-info">
                                        <strong>Separate Sheets</strong>
                                        <span>One sheet per group (requires grouping)</span>
                                    </div>
                                </label>

                                <label className={`output-type-card ${config.outputType === 'simple_sorted' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="outputType"
                                        value="simple_sorted"
                                        checked={config.outputType === 'simple_sorted'}
                                        onChange={(e) => updateConfig({ outputType: e.target.value })}
                                    />
                                    <div className="output-type-icon">
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <line x1="8" y1="9" x2="16" y2="9" />
                                            <line x1="8" y1="12" x2="16" y2="12" />
                                            <line x1="8" y1="15" x2="16" y2="15" />
                                        </svg>
                                    </div>
                                    <div className="output-type-info">
                                        <strong>Simple Sorted</strong>
                                        <span>Basic sorted list without grouping</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Color Scheme</label>
                            <select
                                value={config.colorScheme}
                                onChange={(e) => updateConfig({ colorScheme: e.target.value })}
                                className="form-select"
                            >
                                <option value="default">Default (Blue/Green)</option>
                                <option value="professional">Professional (Gray/Navy)</option>
                                <option value="vibrant">Vibrant (Rainbow)</option>
                                <option value="minimal">Minimal (Black/White)</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SortingGroupingConfig;
